import { useProducers, useProducerSummaries } from '@newfront-insurance/admin-ui/accounts';
import { toMap } from '@newfront-insurance/core';

import type { Employee } from '@/api/types/employee';

export type ProducerSummary = Omit<Employee, 'roles' | 'departments'>;

export interface ProducersMapHook {
  producersMap: Map<string, ProducerSummary>;
}

export interface ProducersWithRolesMapHook {
  producersMap: Map<string, Employee>;
}

export type FilterField = 'userUuid' | 'uuid';

/**
 * Producers keyed by uuid, without auth roles. Use this for name and avatar lookups.
 */
export function useProducersMap(filterField?: FilterField): ProducersMapHook {
  const { data: producers } = useProducerSummaries();

  const producersMap = mapEmployees(producers ?? [], filterField);

  return { producersMap };
}

/**
 * Producers keyed by uuid, including auth roles and the `departments` derived from them. This costs
 * a per-employee role lookup in users-api, so prefer `useProducersMap` unless you read those fields.
 */
export function useProducersWithRolesMap(filterField?: FilterField): ProducersWithRolesMapHook {
  const { data: producers } = useProducers();

  const producersMap = mapEmployees(producers ?? [], filterField);

  return { producersMap };
}

function mapEmployees<T extends ProducerSummary>(
  employeeList: T[],
  filterField: FilterField = 'userUuid',
): Map<string, T> {
  return toMap(employeeList, (employee) => employee[filterField]);
}
